import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import HTMLFlipBook from 'react-pageflip';
import './BookMenu.scss';

const TOTAL_PAGES = 15;
const PAGE_RATIO = 1200 / 1696; // width / height of the rendered menu pages
const pages = Array.from(
  { length: TOTAL_PAGES },
  (_, i) => `/menu-pages/page-${String(i + 1).padStart(2, '0')}.jpg`
);

const BookPage = forwardRef(({ src, number }, ref) => (
  <div className="book-menu__page" ref={ref}>
    <img src={src} alt={`Menu page ${number}`} loading={number <= 3 ? 'eager' : 'lazy'} draggable={false} />
  </div>
));

const getLayout = () => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const spread = vw >= 900 && vw > vh; // two-page spread on wide landscape screens
  const maxH = vh - (spread ? 150 : 130);
  const maxW = spread ? (vw - 120) / 2 : vw - 24;
  const width = Math.floor(Math.min(maxW, maxH * PAGE_RATIO));
  return { spread, width, height: Math.floor(width / PAGE_RATIO) };
};

const BookMenu = () => {
  const bookRef = useRef(null);
  const [layout, setLayout] = useState(getLayout);
  const [page, setPage] = useState(0);
  const [zoomSrc, setZoomSrc] = useState(null);

  useEffect(() => {
    let timer;
    const onResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setLayout(getLayout()), 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const flip = useCallback((dir) => {
    const api = bookRef.current?.pageFlip();
    if (!api) return;
    dir > 0 ? api.flipNext() : api.flipPrev();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') flip(1);
      if (e.key === 'ArrowLeft') flip(-1);
      if (e.key === 'Escape') setZoomSrc(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [flip]);

  const pageLabel = layout.spread && page > 0 && page < TOTAL_PAGES - 1
    ? `${page + 1}–${page + 2}`
    : `${page + 1}`;

  return (
    <div className="book-menu">
      <header className="book-menu__header">
        <img src="/haveli-logo.png" alt="Haveli" className="book-menu__logo" />
        <div>
          <h1>Haveli Restaurant</h1>
          <p>The Royal Experience</p>
        </div>
      </header>

      <div className="book-menu__stage">
        <div
          className="book-menu__frame"
          style={{ width: layout.width * (layout.spread ? 2 : 1), height: layout.height }}
        >
        <HTMLFlipBook
          key={`${layout.width}-${layout.spread}`}
          ref={bookRef}
          width={layout.width}
          height={layout.height}
          size="fixed"
          showCover
          usePortrait={!layout.spread}
          drawShadow
          maxShadowOpacity={0.5}
          flippingTime={700}
          mobileScrollSupport={false}
          startPage={page}
          onFlip={(e) => setPage(e.data)}
          className="book-menu__book"
        >
          {pages.map((src, i) => (
            <BookPage key={src} src={src} number={i + 1} />
          ))}
        </HTMLFlipBook>
        </div>
      </div>

      <nav className="book-menu__controls">
        <button onClick={() => flip(-1)} disabled={page === 0} aria-label="Previous page">‹</button>
        <span className="book-menu__counter">Page {pageLabel} / {TOTAL_PAGES}</span>
        <button onClick={() => setZoomSrc(pages[page])} aria-label="Zoom page" className="book-menu__zoom-btn">⤢</button>
        <button onClick={() => flip(1)} disabled={page >= TOTAL_PAGES - 1} aria-label="Next page">›</button>
      </nav>
      <p className="book-menu__hint">Swipe or tap the page corners to turn · ⤢ to zoom</p>

      {zoomSrc && (
        <div className="book-menu__zoom" onClick={() => setZoomSrc(null)}>
          <button className="book-menu__zoom-close" aria-label="Close">×</button>
          <img src={zoomSrc} alt="Menu page zoomed" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default BookMenu;
